import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const RouterShowcase = () => {
  const sectionRef = useRef(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 968);
  
  const images = [
    { src: "/product.png", alt: "RUT956 Industrial Router" },
    { src: "/rut241-1.png", alt: "RUT241 Router" },
    { src: "/product3.png", alt: "RUTm50 5G Router" }
  ];

  const handleScrollToProducts = (e) => {
    e.preventDefault();
    const nextSection = document.getElementById('all-routers-section');
    if (nextSection) {
      const yOffset = -80;
      const y = nextSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 968);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const imageInterval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % images.length);
    }, 2000);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      clearInterval(imageInterval);
    };
  }, []);

  const styles = {
    container: {
      display: 'flex',
      width: '100%',
      height: isMobile ? 'auto' : '65vh',
      minHeight: isMobile ? '60vh' : 'auto',
      maxWidth: '100vw',
      opacity: '0',
      transform: 'translateY(50px)',
      transition: 'all 0.8s ease-out',
      padding: isMobile ? '10px' : '20px',
      boxSizing: 'border-box',
      overflow: 'hidden',
      marginTop:'20px',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0
    },
    contentSection: {
      flex: '1',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      maxWidth: '100%',
      marginLeft:'30px',
      height: 'auto',
      overflow: 'hidden',
      marginTop: isMobile ? '-40px' : '0'
    },
    imageSection: {
      flex: isMobile ? '0 0 200px' : '0.8', // Reduced from 250px to 200px
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      width: '90%',
      height: isMobile ? '100px' : '100%', // Reduced from 250px to 200px
      order: isMobile ? -1 : 0,
      marginBottom: isMobile ? '50px' : '0' // Added negative margin bottom
    },
    heading: {
      fontSize: isMobile ? '28px' : '40px',
      fontWeight: '600',
      marginBottom: isMobile ? '8px' : '18px',
      color: '#410684',
      marginTop: '0',
      fontFamily: 'Oswald, sans-serif',
      textAlign: isMobile ? 'center' : 'left',
      wordWrap: 'break-word',
      lineHeight: '40px'
    },
    description: {
      fontSize: isMobile ? '14px' : '16px',
      lineHeight: '24px',
      color: '#414042',
      fontFamily:'Inter, sans-serif',
      fontWeight:'400',
      marginBottom: isMobile ? '8px' : '18px',
      maxWidth: '100%',
      textAlign: isMobile ? 'center' : 'left',
      wordWrap: 'break-word'
    },
    features: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginBottom: isMobile ? '8px' : '12px',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    feature: {
      backgroundColor: '#e9ecef',
      padding: '6px 10px',
      borderRadius: '4px',
      fontFamily:'Inter, sans-serif',
      fontSize: isMobile ? '11px' : '13px',
      color: '#414042',
      whiteSpace: 'nowrap'
    },
    linksContainer: {
      display: 'flex',
      gap: '10px',
      marginTop: isMobile ? '8px' : '12px',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    link: {
      textDecoration: 'none',
      padding: isMobile ? '8px 16px' : '12px 32px',
      fontSize: isMobile ? '12px' : '18px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: '#410684', // Purple color for buttons
      color: 'white',
      borderRadius: '4px',
      fontFamily: 'Oswald, sans-serif',
      border: 'none',
      lineHeight: '20px'
    },
    imageWrapper: {
      position: 'absolute',
      width: '100%',
      maxWidth: isMobile ? '160px' : '280px', // Reduced from 180px to 160px
      height: 'auto',
      transition: 'all 0.5s ease-in-out',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    image: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      maxHeight: isMobile ? '180px' : 'none' // Reduced from 220px to 180px
    }
  };

  const combinedFeatures = [
    '4G/LTE (Cat 4)',
    '5G Ready',
    'Dual SIM',
    'Wi-Fi',
    'IP55',
    'PoE-In/Out',
    'RS232/RS485',
    'GPS/GNSS'
  ];

  return (
    <div ref={sectionRef} style={styles.container}>
      <div style={styles.contentSection}>
        <h1 style={styles.heading}>ADVANCED INDUSTRIAL ROUTERS</h1>
        <p style={styles.description}>
          Experience the next generation of industrial connectivity with our comprehensive router lineup. 
          From the robust RUT956 with its advanced IoT capabilities, to the compact yet powerful RUT241, 
          and the future-ready RUTm50 with 5G support - we offer solutions for every industrial networking need. 
          Our routers combine cellular, Wi-Fi, and wired connectivity with automatic WAN failover, 
          enhanced security features including TPM and eSIM support, and speeds up to 3.4 Gbps.
        </p>
        <div style={styles.features}>
          {combinedFeatures.map((feature, index) => (
            <span key={index} style={styles.feature}>{feature}</span>
          ))}
        </div>
        <div style={styles.linksContainer}>
          <Link to="/contactus" style={styles.link}>
            CONTACT US
          </Link>
          <a onClick={handleScrollToProducts} style={styles.link}>
            VIEW ALL ROUTERS
          </a>
        </div>
      </div>
      <div style={styles.imageSection}>
        {images.map((image, index) => (
          <div
            key={index}
            style={{
              ...styles.imageWrapper,
              opacity: activeImageIndex === index ? 1 : 0,
              transform: `translateX(${(index - activeImageIndex) * 100}px)`,
              zIndex: images.length - index
            }}
          >
            <img 
              src={image.src}
              alt={image.alt}
              style={styles.image}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RouterShowcase;